import { logger } from "@scott/logger";
import { getTranslations } from "next-intl/server";
import type { ProductListingBlockClientProps } from "@/components/blocks/content-blocks/product-listing-block.client";
import { ProductListingBlockClient } from "@/components/blocks/content-blocks/product-listing-block.client";
import { getCountryConfig } from "@/lib/server/actions/get-country-config";
import { productListingServerAdapter } from "@/services/contentful/components/ProductListing/product-listing.server-adapter";
import type { ProductListingData } from "@/services/product/get-product-listing";
import type { ProductListEntry } from "@/services/product/get-products-by-codes";
import {
  buildProductListingApiQuery,
  readProductListingQueryState,
  splitProductListingApiQuery,
} from "@/services/product/product-listing-query-state";
import {
  getShopperProductListing,
  getShopperProductsByCode,
} from "@/services/product/shopper-catalogue";
import type { UiConfiguration } from "@/types/page-block";
import type { PageContext } from "@/types/page-context";

export interface ProductListingBlockProps
  extends UiConfiguration,
    Partial<PageContext> {
  sys?: { id: string };
  id?: string;
  internalName: string;
  title?: string;
  anchorId?: string;
  productCategory?: string;
  specificProducts?: ProductListEntry[];
  defaultSort?: string;
  searchQuery?: string;
  variant?: "grid" | "carousel";
  showCarouselNavigationArrows?: boolean;
  showFilters?: boolean;
  showSorting?: boolean;
  paginationMode?: "paginate" | "showAll";
  pageSize?: number;
  country?: string;
  language?: string;
}

export async function buildProductListingClientProps(
  props: Readonly<ProductListingBlockProps>,
  componentName: string
): Promise<ProductListingBlockClientProps | null> {
  const hasSpecificProducts = (props.specificProducts?.length ?? 0) > 0;

  if (!(hasSpecificProducts || props.productCategory)) {
    logger.warn(
      `${componentName}: no productCategory or specificProducts configured in Contentful`,
      { internalName: props.internalName }
    );
    return null;
  }

  const t = await getTranslations("productListing");
  const microcopies = {
    applyFilters: t("applyFilters"),
    cardBadges: props.pageContext?.microcopies?.productListingBadges,
    cardAdd: t("cardAdd"),
    cardCompare: t("cardCompare"),
    cardConfigure: t("cardConfigure"),
    clearAllFilters: t("clearAllFilters"),
    closeFilters: t("closeFilters"),
    closeSort: t("closeSort"),
    emptyState: t("emptyState"),
    filterProducts: t("filters"),
    filterTitle: t("filterTitle"),
    loadingProducts: t("loadingProducts"),
    loadMore: t("loadMore"),
    nextSlide: t("nextSlide"),
    noFilterOptionsAvailable: t("noFilterOptionsAvailable"),
    previousSlide: t("previousSlide"),
    rangeMaximum: t("rangeMaximum"),
    rangeMinimum: t("rangeMinimum"),
    removeFilter: t("removeFilter"),
    retry: t("retry"),
    sortPriceAscending: t("sort.priceAscending"),
    sortPriceDescending: t("sort.priceDescending"),
    sortProducts: t("sortProducts"),
    sortTitle: t("sortTitle"),
    sortRelevant: t("sort.relevant"),
    unableToLoadProducts: t("unableToLoadProducts"),
  };

  const listingId =
    props.sys?.id ?? props.id ?? props.anchorId ?? props.internalName;
  const routeContext = { country: props.country, language: props.language };
  const countryConfig = await getCountryConfig(props.country);
  const baseSiteId = countryConfig?.siteUid ?? "";
  const uiConfig = {
    backgroundColor: props.backgroundColor,
    paddingTop: props.paddingTop,
    paddingBottom: props.paddingBottom,
  };

  // Specific product list mode — takes priority over category
  if (hasSpecificProducts) {
    const specificProducts = props.specificProducts ?? [];
    const apiData = await getShopperProductsByCode(
      specificProducts,
      props.language
    );

    const presentation = {
      baseQuery: "",
      variant: props.variant,
      showCarouselNavigationArrows: props.showCarouselNavigationArrows,
      paginationMode: "showAll" as const,
      showFilters: false,
      showSorting: false,
    };

    const uiProps = productListingServerAdapter(
      apiData,
      uiConfig,
      microcopies,
      presentation,
      props.title,
      props.sys?.id,
      props.anchorId,
      routeContext,
      baseSiteId
    );

    const productCodes = apiData.products
      .map((p) => p.code)
      .filter((c): c is string => c !== undefined);

    const emptyQueryState = { currentPage: 0, filters: "", sort: "" };

    return {
      adapterContext: {
        anchorId: props.anchorId,
        baseSiteId,
        microcopies,
        presentation,
        routeContext,
        sysId: props.sys?.id,
        title: props.title,
        uiConfig,
      },
      defaultQueryState: emptyQueryState,
      initialError: false,
      initialQueryState: emptyQueryState,
      listingId,
      productCodes,
      queryContext: {
        categoryId: undefined,
        language: props.language,
        loadAll: true,
        pageSize: apiData.count,
      },
      uiProps,
    };
  }

  // Category mode — existing behaviour unchanged
  const configuredSort = props.defaultSort || undefined;
  const configuredQuery = splitProductListingApiQuery(
    props.searchQuery || undefined,
    configuredSort ? [configuredSort] : []
  );
  const defaultQueryState = {
    currentPage: 0,
    filters: configuredQuery.filters,
    sort: configuredSort ?? configuredQuery.sort,
  };
  const requestedQueryState = readProductListingQueryState(
    new URLSearchParams(props.pageContext?.searchParams),
    listingId,
    defaultQueryState
  );

  const result = await getShopperProductListing({
    categoryId: props.productCategory,
    currentPage: requestedQueryState.currentPage,
    query: buildProductListingApiQuery(
      requestedQueryState.filters,
      requestedQueryState.sort
    ),
    sort: requestedQueryState.sort,
    language: props.language,
    loadAll: props.paginationMode === "showAll",
    pageSize: props.pageSize,
  });

  if (result.status === "error") {
    logger.error(`${componentName}: failed to load products`, {
      error: result.error,
      categoryId: props.productCategory,
    });
  }

  const initialError = result.status === "error";
  const apiData: ProductListingData =
    result.status === "success"
      ? result.data
      : { breadcrumbs: [], count: 0, facets: [], products: [], sorts: [] };

  const presentation = {
    baseQuery: defaultQueryState.filters,
    variant: props.variant,
    showCarouselNavigationArrows: props.showCarouselNavigationArrows,
    paginationMode: props.paginationMode ?? "paginate",
    showFilters: props.showFilters ?? true,
    showSorting: props.showSorting ?? true,
  };

  const uiProps = productListingServerAdapter(
    apiData,
    uiConfig,
    microcopies,
    presentation,
    props.title,
    props.sys?.id,
    props.anchorId,
    routeContext,
    baseSiteId
  );

  const productCodes = apiData.products
    .map((product) => product.code)
    .filter((code): code is string => code !== undefined);
  const effectiveSort =
    requestedQueryState.sort ??
    apiData.pagination?.sort ??
    apiData.sorts.find((sort) => sort.selected)?.code;

  return {
    adapterContext: {
      anchorId: props.anchorId,
      baseSiteId,
      microcopies,
      presentation,
      routeContext,
      sysId: props.sys?.id,
      title: props.title,
      uiConfig,
    },
    defaultQueryState,
    initialError,
    initialQueryState: { ...requestedQueryState, sort: effectiveSort },
    listingId,
    productCodes,
    queryContext: {
      categoryId: props.productCategory,
      language: props.language,
      loadAll: presentation.paginationMode === "showAll",
      pageSize: apiData.pagination?.pageSize ?? props.pageSize,
    },
    uiProps,
  };
}

export async function ProductListingBlock(
  props: Readonly<ProductListingBlockProps>
) {
  const clientProps = await buildProductListingClientProps(
    props,
    "ProductListingBlock"
  );
  if (!clientProps) {
    return null;
  }
  return <ProductListingBlockClient {...clientProps} />;
}
